// src/pages/markets/board/card/EventCard.tsx
//
// Invariant: the card shows exactly one score at a time, and the displayed
// (market_id, group) pair is always a real row from conviction_scores.
//
// The event image and the open icon are real <a href target="_blank"> links —
// ctrl-click, middle-click and open-in-new-tab work natively; a plain left click
// is intercepted and onOpen opens the tab, so the board stays where it is.
// Every interactive child prevents default AND stops propagation: inside an
// anchor, stopPropagation alone still lets the browser follow the href.

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
    BoardScore,
    EventConvictionIndex,
    MarketSeries,
    ScreenerEvent,
} from '../api/boardTypes';
import {
    findScore,
    isSingleMarketEvent,
    marketsForGroup,
    primaryMarketFor,
} from '../api/scoreIndex';
import { useCardSeries } from '../api/useSeries';
import { seriesSpanMs } from './trendGeometry';
import { usePrefetchTraders } from '../api/useTraders';
import { CardHeader } from './CardHeader';
import { CardRow } from './CardRow';
import { OtherGroupBadge } from './OtherGroupBadge';
import type { ActivityContext } from './ActivityBlock';
import { ViewBody } from './body/ViewBody';
import { TrendBody } from './body/TrendBody';
import {
    BODY_HEIGHT,
    BODY_PADDING_Y,
    CARD_HEIGHT,
    CARD_TEST_IDS,
    FOOTER_HEIGHT,
    ROW_HEIGHT,
    STALE_AFTER_S,
    bodyMetrics,
    type BodyShape,
} from './cardLayout';
import { ageSeconds, formatAge } from './cardFormat';
import { type CardVariant } from '../board/columnConfig';
import { useBoardGroups } from '../state/boardGroupsContext';
import type { BodyMode } from '../board/columnConfig';
import { eventHref } from '../board/routes';
import { HoverPanel } from './HoverPanel';
import { EventOutcomesPanel } from './EventOutcomesPanel';

const PANEL_OPEN_DELAY_MS = 150;

export interface EventCardProps {
    event: ScreenerEvent;
    scores: BoardScore[];
    index?: EventConvictionIndex;
    seriesByMarket?: Map<string, MarketSeries>;
    // isFetching, NOT isLoading: the batch keeps the previous block's data
    // through a key change, so isLoading reads false exactly while the new
    // batch is in flight and an absent market means "not answered yet".
    seriesFetching?: boolean;
    /** Market ids the column's batch asked about, so an absent row reads as
     *  "asked, and there is none" rather than "not asked". */
    seriesCovered?: readonly string[];
    defaultGroup: string;
    bodyVariant: CardVariant;
    showGroupToggle: boolean;
    cardMetrics: string[];
    bodyMode: BodyMode;
    trendWindow: string;
    now: number;
    activityCtx?: ActivityContext | null;
    collapsed?: boolean;
    onToggleCollapsed?: (eventId: string) => void;
    onOpen: (eventId: string, slug?: string | null) => void;
    // EventCardProps
    isNew?: boolean;
    onSeen?: (eventId: string) => void;
    activeTags?: readonly string[] | null;
    /** The groups the column shows scores for, ahead of any extra group the
     *  event happens to carry. Empty is legitimate: the toggle then offers
     *  exactly the groups that scored this event. */
    scoreGroups?: readonly string[];
}

function EventCardInner({
    event,
    scores,
    index,
    seriesByMarket,
    seriesFetching,
    seriesCovered,
    defaultGroup,
    bodyVariant,
    showGroupToggle,
    cardMetrics,
    bodyMode,
    trendWindow,
    now,
    activityCtx,
    collapsed,
    onToggleCollapsed,
    onOpen,
    isNew,
    onSeen,
    activeTags,
    scoreGroups = [],
}: EventCardProps) {
    const theme = useTheme();
    const boardGroups = useBoardGroups();
    const prefetchTraders = usePrefetchTraders();

    const availableGroups = useMemo(
        () => new Set(scores.map(s => s.group)),
        [scores]
    );

    const toggleGroups = useMemo(() => {
        const extra = [...availableGroups].filter(g => !scoreGroups.includes(g));
        return [...scoreGroups, ...extra];
    }, [availableGroups, scoreGroups]);

    const [marketId, setMarketId] = useState<string | null>(null);

    const group = defaultGroup;
    // One read for the three places the card accents itself, instead of three
    // lookups into a two-entry map that fell back to a warning colour for the
    // other sixteen groups.
    const accent = boardGroups.resolve(group).color;
    const groupScores = useMemo(
        () => scores.filter(s => s.group === group),
        [scores, group]
    );

    // Without a score for this group the stepper still has to go somewhere, so
    // it walks the event's own markets — the New column is mostly this case.
    const stepMarkets = useMemo(() => {
        const scored = marketsForGroup(scores, group);
        if (scored.length > 0) return scored;
        return [...event.markets]
            .sort(
                (a, b) =>
                    Number(b.is_open) - Number(a.is_open) ||
                    (b.volume ?? 0) - (a.volume ?? 0)
            )
            .map(m => m.market_id);
    }, [scores, group, event.markets]);

    // The activity scope decides which market the card opens on: the column
    // ranked this event on that market, so landing anywhere else shows numbers
    // the reader did not sort by.
    const primary =
        activityCtx?.activity?.scope_market_id ??
        primaryMarketFor(index, scores, group) ??
        event.primary_market_id;
    const activeMarketId = marketId ?? primary ?? stepMarkets[0] ?? null;

    const score = findScore(scores, group, activeMarketId);
    const market =
        event.markets.find(m => m.market_id === activeMarketId) ?? null;

    const jumpMarket = activityCtx?.activity?.jump_market_id ?? null;
    const jumpLabel = useMemo(() => {
        if (!jumpMarket) return null;
        const m = event.markets.find(x => x.market_id === jumpMarket);
        return m?.outcome_yes_label ?? m?.question ?? null;
    }, [jumpMarket, event.markets]);

    const batchedSeries = activeMarketId
        ? seriesByMarket?.get(activeMarketId) ?? null
        : null;
    // The single-market body draws a price line in view mode too, so it wants a
    // series even when the column is not in trend mode. The New column does not.
    const wantsSeries =
        bodyMode === 'trend' ||
        (bodyVariant !== 'meta' && isSingleMarketEvent(event));
    // Waiting on the batch, not racing it: firing per card while the column's
    // one request is in flight is N requests for the same rows, every scroll.
    // The batch settled and it was asked about this market. Whether it came
    // back with a row or not, that is our answer — asking again would be the
    // same storm one round trip later.
    const batchCovered =
        !seriesFetching &&
        Boolean(activeMarketId) &&
        Boolean(seriesCovered?.includes(activeMarketId as string));
    const needsOwnSeries =
        wantsSeries &&
        Boolean(activeMarketId) &&
        !batchedSeries &&
        !seriesFetching &&
        !batchCovered;
    const ownSeries = useCardSeries(
        activeMarketId,
        trendWindow,
        needsOwnSeries
    );
    const fetched = batchedSeries ?? ownSeries.series;
    // undefined is "nobody has answered yet", null is "answered, and there is
    // none". Only the second earns a compact card.
    const answered =
        Boolean(batchedSeries) || batchCovered || ownSeries.hasData;
    const series = fetched ?? (answered ? null : undefined);

    // Nothing to plot means nothing to reserve: a single-market card with no
    // price line gives the space back instead of holding an empty frame.
    const shape: BodyShape =
        bodyVariant === 'meta'
            ? 'meta'
            : bodyVariant === 'activity'
              ? 'activity'
              : 'score';
    // Not "did a series arrive" but "can one be drawn": the endpoint can answer
    // with a single point, which makes no line and used to leave the frame up.
    const drawsLine = useMemo(
        () => Boolean(series && seriesSpanMs(series.points)),
        [series]
    );
    const compactBody =
        bodyMode !== 'trend' &&
        isSingleMarketEvent(event) &&
        series !== undefined &&
        !drawsLine;
    const metrics = bodyMetrics(shape, compactBody);

    const age = ageSeconds(score?.ts, now);
    const stale = age !== null && age > STALE_AFTER_S;

    const conflictEntry = index?.by_group?.find(g => g.group === group);
    const conflict = Boolean(
        conflictEntry &&
            conflictEntry.yes_count > 0 &&
            conflictEntry.no_count > 0
    );

    const handleSurfaceToggle = useCallback(
        (e: React.MouseEvent) => {
            // A child that handled the click already called preventDefault.
            if (e.defaultPrevented) return;
            if (
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey ||
                e.button !== 0
            )
                return;
            onToggleCollapsed?.(event.event_id);
        },
        [onToggleCollapsed, event.event_id]
    );

    const handleSurfaceKey = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            onToggleCollapsed?.(event.event_id);
        },
        [onToggleCollapsed, event.event_id]
    );

    // Modified clicks stay with the browser — the image is still a real href.
    const handleImageOpen = useCallback(
        (e: React.MouseEvent) => {
            if (
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey ||
                e.button !== 0
            )
                return;
            e.preventDefault();
            e.stopPropagation();
            if (isNew) onSeen?.(event.event_id);
            onOpen(event.event_id, event.slug);
        },
        [onOpen, isNew, onSeen, event.event_id, event.slug]
    );

    const handleRootEnter = useCallback(() => {
        if (isNew) onSeen?.(event.event_id);
        if (score) prefetchTraders(score.market_id, score.group);
    }, [isNew, onSeen, event.event_id, score, prefetchTraders]);

    const handleToggle = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleCollapsed?.(event.event_id);
        },
        [onToggleCollapsed, event.event_id]
    );

    const scoreEmptyText = score ? '' : 'No position in this market';

    const renderPanel = () => (
        <EventOutcomesPanel
            event={event}
            scores={bodyVariant === 'meta' ? scores : groupScores}
            accentColor={accent}
            now={now}
            variant={bodyVariant}
            activityCtx={activityCtx}
            index={index}
            activeGroup={group}
            groupTitle={boardGroups.titleOf(group)}
            activeTags={activeTags}
        />
    );

    const modeLabel =
        event.market_mode === 'dependent'
            ? 'winner takes all'
            : event.market_mode === 'independent'
              ? 'independent'
              : 'single';

    const footerAge =
        bodyVariant === 'activity'
            ? activityCtx?.activity
                ? formatAge(activityCtx.activity.computed_at, now)
                : 'no activity'
            : bodyVariant === 'meta'
              ? `${event.market_count} ${event.market_count === 1 ? 'outcome' : 'outcomes'} · ${modeLabel}${
                    event.open_market_count < event.market_count
                        ? ` · ${event.open_market_count} open`
                        : ''
                }`
              : score
                ? formatAge(score.ts, now)
                : 'no score';

    const surfaceSx = {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'background.default',
        borderRadius: '4px',
        boxShadow: `inset 3px 0 0 transparent, inset 0 0 0 1px ${theme.palette.divider}`,
        transition: 'outline-color 400ms ease, box-shadow 400ms ease',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
        '&:focus-visible': {
            outline: `1px solid ${theme.palette.primary.main}`,
            outlineOffset: '-1px',
        },
        ...(isNew && {
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}, inset 0 0 0 1px ${theme.palette.divider}`,
        }),
    } as const;

    if (collapsed) {
        return (
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={false}
                data-testid={CARD_TEST_IDS.root}
                onClick={handleSurfaceToggle}
                onKeyDown={handleSurfaceKey}
                onMouseEnter={handleRootEnter}
                style={{ height: ROW_HEIGHT }}
                sx={surfaceSx}
            >
                <HoverPanel
                    disabled={event.markets.length === 0}
                    placement="right-start"
                    padded={false}
                    openDelayMs={PANEL_OPEN_DELAY_MS}
                    content={renderPanel}
                    sx={{ height: '100%' }}
                >
                    <CardRow
                        event={event}
                        score={score}
                        scores={groupScores}
                        variant={bodyVariant}
                        now={now}
                        activityCtx={activityCtx}
                        activeTags={activeTags}
                        onOpenEvent={handleImageOpen}
                        onToggle={onToggleCollapsed ? handleToggle : undefined}
                    />
                </HoverPanel>
            </Box>
        );
    }

    return (
        <Box
            data-testid={CARD_TEST_IDS.root}
            onMouseEnter={handleRootEnter}
            style={{
                height: bodyMode === 'trend' ? CARD_HEIGHT : metrics.cardHeight,
            }}
            sx={{ ...surfaceSx, cursor: 'default' }}
            role="button"
            tabIndex={0}
            onClick={handleSurfaceToggle}
            onKeyDown={handleSurfaceKey}
        >
            <CardHeader
                event={event}
                now={now}
                onToggle={onToggleCollapsed ? handleToggle : undefined}
                onOpenEvent={handleImageOpen}
                badge={<OtherGroupBadge index={index} activeGroup={group} />}
                panel={event.markets.length > 0 ? renderPanel : undefined}
            />

            <Box
                data-testid={CARD_TEST_IDS.body}
                style={{
                    height:
                        bodyMode === 'trend' ? BODY_HEIGHT : metrics.bodyHeight,
                    paddingTop: BODY_PADDING_Y,
                    paddingBottom: BODY_PADDING_Y,
                }}
                sx={{ px: 1.25, boxSizing: 'border-box', overflow: 'hidden' }}
            >
                {bodyMode === 'trend' ? (
                    <TrendBody
                        market={market}
                        series={series ?? null}
                        impliedProb={null}
                        groupColor={accent}
                        window={trendWindow}
                        loading={ownSeries.isLoading}
                        error={Boolean(ownSeries.error)}
                    />
                ) : (
                    <ViewBody
                        event={event}
                        score={score}
                        groupScores={groupScores}
                        allScores={scores}
                        activeTags={activeTags}
                        accentColor={accent}
                        selectedMarketId={activeMarketId}
                        now={now}
                        variant={bodyVariant}
                        activityCtx={activityCtx}
                        activityMarketLabel={jumpLabel}
                        scoreEmptyText={scoreEmptyText}
                        onSelectMarket={setMarketId}
                        series={series}
                        seriesWindow={trendWindow}
                        marketBlockHeight={metrics.block}
                    />
                )}
            </Box>

            <Box
                data-testid={CARD_TEST_IDS.footer}
                style={{ height: FOOTER_HEIGHT }}
                sx={{
                    px: 1.25,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: stale ? 'warning.main' : 'text.disabled',
                    }}
                >
                    {footerAge}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    {event.category ?? ''}
                </Typography>
            </Box>
        </Box>
    );
}

export const EventCard = memo(EventCardInner);
